import type { Table } from '@tanstack/react-table';
import { CircleCheckBig, X } from 'lucide-react';
import { memo, useCallback } from 'react';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { IOneItemInPrepareForRequest } from '@/pages/PurchasesContainer/PurchasesRequests/api/Request.interface';
import { cn } from 'lib/utils';

type Props = {
  table: Table<IOneItemInPrepareForRequest>;
  isLastUpdate: 'true' | 'false';
  setIsLastUpdate: React.Dispatch<React.SetStateAction<'true' | 'false'>>;
};

const UPDATE_TYPES = [
  {
    value: 'true',
    label: 'اخر تحديث',
    bg: 'bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
    icon: <CircleCheckBig className="h-4 w-4" />,
  },
  {
    value: 'false',
    label: 'غير محدث',
    bg: 'bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
    icon: <X className="h-4 w-4" />,
  },
];

function LastUpdateFilter({ table, isLastUpdate, setIsLastUpdate }: Props) {
  const handleUpdateTypeChange = useCallback(
    (value: 'true' | 'false') => {
      setIsLastUpdate(value || 'true');
      table.getColumn('isLastUpdate')?.setFilterValue(value || 'true');
    },
    [setIsLastUpdate, table],
  );

  return (
    <div className="space-y-1 rounded-lg border bg-white/50 p-1.5 shadow-sm backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/50">
      <div className="flex items-center justify-between">
        <h3 className="text-2xs font-semibold text-gray-900 dark:text-gray-100">
          حالة التحديث
        </h3>
        {isLastUpdate === 'false' && (
          <span className="rounded-full bg-red-100 px-1 py-0.5 text-2xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
            مفعل
          </span>
        )}
      </div>
      <ToggleGroup
        value={isLastUpdate}
        onValueChange={(value) =>
          handleUpdateTypeChange(value as 'true' | 'false')
        }
        type="single"
        className="grid grid-cols-2 gap-1"
      >
        {UPDATE_TYPES.map((item) => (
          <ToggleGroupItem
            key={item.value}
            value={item.value}
            aria-label={item?.label}
            className={cn(
              'group relative flex h-6 items-center justify-between overflow-hidden rounded-md border p-0 transition-all duration-200 hover:scale-[1.02] hover:shadow-md',
              item.border,
              item.value === isLastUpdate &&
                'border-2 border-primary shadow-sm ring-1 ring-primary/20',
            )}
          >
            <div
              className={cn(
                'flex h-full w-6 items-center justify-center border-l transition-colors duration-200',
                item.bg,
                item.border,
                item.text,
              )}
            >
              {item.icon}
            </div>
            <div className="flex-1 text-center text-[10px] font-medium text-gray-700 transition-colors duration-200 group-hover:text-gray-900 dark:text-gray-300 dark:group-hover:text-gray-100">
              {item?.label}
            </div>
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

export default memo(LastUpdateFilter);
